(() => {
  'use strict';
  if (window.__belstarterRapidSubtitlesV1) return;
  window.__belstarterRapidSubtitlesV1 = true;
  const cardSelector = '.rps-product-container';
  const subtitleSelector = '[data-belstarter-rapid-subtitle]';
  const cache = new Map();
  const states = new WeakMap();
  const pending = [];
  let active = 0;
  let disabled = false;
  let scheduled = false;

  function pathFor(card) {
    const link = card.querySelector('a.rps-product-link[href]');
    if (!link) return '';
    try {
      const url = new URL(link.href, location.origin);
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return '';
      return url.pathname.replace(/\/$/, '');
    } catch (_) {
      return '';
    }
  }

  function current(card, state) {
    return card.isConnected && states.get(card) === state
      && pathFor(card) === state.path
      && (card.getAttribute('data-product-id') || '') === state.id;
  }

  function valid(data, path) {
    try {
      return data && data.format === 'belstarter-subtitle-v1'
        && typeof data.subtitle === 'string'
        && decodeURIComponent(path.split('/').pop()) === data.handle;
    } catch (_) {
      return false;
    }
  }

  function apply(card, state, data) {
    if (!current(card, state) || !valid(data, state.path)) return false;
    if (state.id && String(data.id) !== state.id) return false;
    const title = card.querySelector('.rps-product-title');
    if (!title) return false;
    clearTimeout(state.timer);
    state.done = true;
    state.data = data;
    const text = data.subtitle.trim();
    const existing = card.querySelector(subtitleSelector);
    if (!text) {
      if (existing) existing.remove();
      return true;
    }
    if (existing && existing.dataset.productPath === state.path && existing.textContent === text) return true;
    if (existing) existing.remove();
    const subtitle = document.createElement('p');
    subtitle.dataset.belstarterRapidSubtitle = '';
    subtitle.dataset.productPath = state.path;
    subtitle.textContent = text;
    subtitle.title = text;
    Object.assign(subtitle.style, {
      display: 'block', margin: '-4px 0 0', padding: '0',
      fontSize: '14px', fontWeight: '400', lineHeight: '1.4',
      color: '#555555', minWidth: '0', maxWidth: '100%',
      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'
    });
    title.insertAdjacentElement('afterend', subtitle);
    return true;
  }

  function fromPrice(card, state) {
    try {
      return apply(card, state, JSON.parse(card.dataset.belstarterSubtitleResponse || 'null'));
    } catch (_) {
      return false;
    }
  }

  function pump() {
    while (active < 4 && pending.length) {
      const job = pending.shift();
      active += 1;
      Promise.resolve().then(async () => {
        if (disabled) return null;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        try {
          const url = new URL(job.path, location.origin);
          url.searchParams.set('view', 'belstarter-subtitle');
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) return null;
          const source = await response.text();
          const node = new DOMParser().parseFromString(source, 'text/html').getElementById('belstarter-subtitle-data');
          if (!node) {
            disabled = true;
            return null;
          }
          const data = JSON.parse(node.textContent);
          return valid(data, job.path) ? data : null;
        } catch (_) {
          return null;
        } finally {
          clearTimeout(timer);
        }
      }).then(job.resolve).finally(() => {
        active -= 1;
        pump();
      });
    }
  }

  function getData(path) {
    if (!cache.has(path)) {
      cache.set(path, new Promise(resolve => pending.push({ path, resolve })));
      pump();
    }
    return cache.get(path);
  }

  function fallback(card, state) {
    if (!current(card, state) || state.done || state.requested) return;
    clearTimeout(state.timer);
    state.requested = true;
    getData(state.path).then(data => {
      if (!current(card, state)) return;
      if (!fromPrice(card, state)) apply(card, state, data);
    });
  }

  function priceCanSupply() {
    const node = document.getElementById('belstarter-search-price-config');
    if (!node) return false;
    try {
      const config = JSON.parse(node.textContent);
      return config.enabled === true && config.providesSubtitles === true;
    } catch (_) {
      return false;
    }
  }

  function load(card) {
    const state = states.get(card);
    if (!state || !current(card, state) || state.started) return;
    state.started = true;
    if (fromPrice(card, state)) return;
    if (priceCanSupply() && card.dataset.belstarterSubtitleFallback !== state.path) {
      state.timer = setTimeout(() => fallback(card, state), 15000);
    } else {
      fallback(card, state);
    }
  }

  const intersection = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        intersection.unobserve(entry.target);
        load(entry.target);
      });
    }, { rootMargin: '200px' })
    : null;

  function scan() {
    document.querySelectorAll(cardSelector).forEach(card => {
      const path = pathFor(card);
      if (!path) return;
      const id = card.getAttribute('data-product-id') || '';
      const previous = states.get(card);
      if (previous && previous.path === path && previous.id === id) {
        if (!fromPrice(card, previous) && previous.data) apply(card, previous, previous.data);
        return;
      }
      if (previous) clearTimeout(previous.timer);
      const existing = card.querySelector(subtitleSelector);
      if (existing) existing.remove();
      const state = { path, id, done: false, started: false, requested: false, timer: null };
      states.set(card, state);
      if (fromPrice(card, state)) return;
      if (intersection) intersection.observe(card);
      else load(card);
    });
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      scan();
    }, 120);
  }

  function start() {
    document.addEventListener('belstarter:subtitle-ready', event => {
      const card = event.target.closest(cardSelector);
      const state = card && states.get(card);
      if (state) fromPrice(card, state);
      else schedule();
    });
    document.addEventListener('belstarter:subtitle-fallback', event => {
      const card = event.target.closest(cardSelector);
      const state = card && states.get(card);
      if (state && state.started) fallback(card, state);
    });
    new MutationObserver(schedule).observe(document.body, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['href', 'data-product-id']
    });
    scan();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
